import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MessageBroadcastingService} from '../message-broadcasting.service';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatDialogTitle, MatDialogClose, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {SaveBroadcastThreadRequest} from '../../api/models/save-broadcast-thread-request';
import {BroadcastContactModel} from '../../api/models/broadcast-contact-model';
import {NotificationService} from '../../notification/notification.service';
import {BroadcastThreadModel} from '../../api/models/broadcast-thread-model';
import {BroadcastContactsFilterDialogComponent} from '../broadcast-contacts-filter/broadcast-contacts-filter-dialog.component';
import {ContactsRequest} from '../../api/models/contacts-request';
import {decompressFromEncodedURIComponent} from 'lz-string';
import {ContactService} from '../../contact/contact.service';
import {FilterOptionModel} from '../../dialogs/grid-filter/filter-option.model';
import { FlexModule } from '@angular/flex-layout';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { NgIf } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTextInputComponent } from '../../editors/mat-text-input/mat-text-input.component';
import { MatRecipientsMultiselectComponent } from '../../editors/mat-recipients-multiselect/mat-recipients-multiselect.component';
import { MatLabel } from '@angular/material/form-field';
import { GridFilterComponent } from '../../dialogs/grid-filter/grid-filter.component';
import { FilterSecondRowAdditionalContentComponent } from '../../dialogs/grid-filter/filter-second-row-additional-content.component';
import { Angular2PromiseButtonModule } from 'angular2-promise-buttons';

export interface SaveBroadcastThreadDialogData {
  phoneNumber: string;
  broadcastId: string;
}

@Component({
    selector: 'app-save-broadcast-thread-dialog',
    templateUrl: './save-broadcast-thread-dialog.component.html',
    styleUrls: ['./save-broadcast-thread-dialog.component.scss'],
    standalone: true,
    imports: [
        MatDialogTitle,
        FlexModule,
        MatIconButton,
        MatDialogClose,
        MatIcon,
        CdkScrollable,
        MatDialogContent,
        NgIf,
        MatProgressSpinner,
        FormsModule,
        ReactiveFormsModule,
        MatTextInputComponent,
        MatRecipientsMultiselectComponent,
        MatLabel,
        GridFilterComponent,
        FilterSecondRowAdditionalContentComponent,
        MatButton,
        MatDialogActions,
        Angular2PromiseButtonModule,
    ],
})
export class SaveBroadcastThreadDialogComponent implements OnInit {

  form: UntypedFormGroup;
  broadcastName = new UntypedFormControl('', [Validators.required]);
  unsubscribeKeyword = new UntypedFormControl('', []);
  contacts = new UntypedFormControl([], []);
  tags = new UntypedFormControl([], []);

  filterOptions: FilterOptionModel[] = [];

  editModel: BroadcastThreadModel;
  customContactsFilter: ContactsRequest
  customContactsQuery = '';
  buttonSubmitPromise: Promise<void>;
  submitted: boolean;
  isLoading = true;

  constructor(
    public broadcastingService: MessageBroadcastingService,
    public notificationService: NotificationService,
    public contactService: ContactService,
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<SaveBroadcastThreadDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SaveBroadcastThreadDialogData,
    private fb: UntypedFormBuilder,
  ) {

  }

  ngOnInit() {
    this.createForm();

    this.asyncInit().then();


  }

  async asyncInit() {
    this.filterOptions = await this.contactService.initFilterOptions();

    if (this.data.broadcastId === 'new') {
      this.editModel = {
        primaryNumber: this.data.phoneNumber,
        id: 'new',
        tags: [],
        contacts: [],
        name: '',
        unsubscribeCode: 'STOP'
      };
      this.isLoading = false;

    } else {
      // get it from server
      this.editModel = await this.broadcastingService.getBroadcastThread(this.data.broadcastId);
      this.customContactsFilter = this.editModel.contactsFilter;
      this.extractContactsFilterQuery();

      this.resetForm();
      this.isLoading = false;

    }
  }

  createForm(): void {
    this.form = this.fb.group({
        'broadcastName': this.broadcastName,
        'unsubscribeKeyword': this.unsubscribeKeyword,
        'contacts': this.contacts,
        'tags': this.tags,
      }
    );
  }

  resetForm() {
    this.form.reset({
      broadcastName: this.editModel.name,
      unsubscribeKeyword: this.editModel.unsubscribeCode,
      contacts: this.editModel.contacts,
      tags: this.editModel.tags
    });
  }

  async onSubmit() {
    if (this.form.valid) {
      this.buttonSubmitPromise = this.processSubmit();
    }
  }

  async processSubmit(): Promise<void> {
    try {
      const reqData = this.prepareFormModel();
      const saveResult = await this.broadcastingService.saveBroadcastThread(reqData);
      if (saveResult.data) {
        this.notificationService.notifySuccess('The changes have been submitted successfully');

        this.dialogRef.close(saveResult.data);
      } else {
        this.notificationService.notifyDanger('Unable to save broadcast');
      }
    } catch (e) {
      console.error('unable to save broadcast', e);
    } finally {
      this.submitted = false;
    }
  }

  prepareFormModel(): SaveBroadcastThreadRequest {
    const formModel = this.form.value;

    const tags: string[] = formModel.tags.map(x => x.tagId);
    const contacts: BroadcastContactModel[] = formModel.contacts.map(x => {
      return {
        contactId: x.contactId,
        contactNumber: x.phoneNumber
      };
    });

    return {
      id: this.editModel.id,
      name: formModel.broadcastName,
      unsubscribeCode: formModel.unsubscribeKeyword,
      tags: tags,
      contacts: contacts,
      primaryNumber: this.editModel.primaryNumber,
      contactsFilter: this.customContactsFilter,
    };
  }

  openContactsList($event: MouseEvent) {
    // open contacts list
    const dialogRef = this.dialog.open(BroadcastContactsFilterDialogComponent, {
      width: '90%',
      height: '90%',
      data: {
        selectedFilter: this.customContactsFilter
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        console.log('contacts selected', result);
        this.customContactsFilter = result;
        this.extractContactsFilterQuery();
      }
    });
  }

  extractContactsFilterQuery() {
    if(this.customContactsFilter?.filterQuery) {
      this.customContactsQuery = decompressFromEncodedURIComponent(this.customContactsFilter.filterQuery);
    } else {
      this.customContactsQuery = '';
    }
  }
}
